import type {Maybe} from '@/types'
import {defaultApi} from '@/axios'
import {isNotNullOrUndefined} from '@/utils/utils'
import ExpiryCookie from '@/utils/ExpiryCookie'
import type {Page, PageableRequest} from '@/types/pagination'
import type {InvitationConfirmRequest} from '@/client/InvitationClient'

const MIN_PWD_LENGTH = 8
const REFRESH_BEFORE_MS = 5 * 60 * 1000

export interface AuthUser {
  id: number
  passwordResetActive: boolean
  roles: string[]
  username: string
}

export interface NewPassword {
  newPassword: string
  newPasswordConfirmation: string
}

export interface ChangePassword extends NewPassword {
  oldPassword: string
}

export interface ResetPassword extends NewPassword {
  token: string
}

export interface EnabledUser {
  id: number
  username: string
  enabled: boolean
  roles: string[]
  companies: number[]
  mfaRequired: boolean
}

export interface UserRequest {
  firstname: string
  lastname: string
  email: string
  language: string
  password: Record<'newPassword' | 'newPasswordConfirmation', string>
}

interface UserCreateRequest {
  invitation: InvitationConfirmRequest
  user: Maybe<UserRequest>
}

export enum Role {
  USER = 'USER',
  HOTEL_ADMIN = 'HOTEL_ADMIN',
  ADMIN = 'ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
}

let refreshTimeoutId: number | undefined = undefined

class AuthService {
  #validateNewPassword(data: NewPassword) {
    if (data.newPassword.trim().length < MIN_PWD_LENGTH) throw Error('userSettings.changePassword.tooShort')
    if (data.newPassword !== data.newPasswordConfirmation) throw Error('userSettings.changePassword.notMatching')
  }

  _doRefresh() {
    refreshTimeoutId = undefined
    this.refresh()
      .then(() => this.scheduleRefresh())
      .catch(() => this.logout())
  }

  fetchCurrentUser(): Promise<AuthUser> {
    return defaultApi.get<AuthUser>('/api/auth/me').then(({data}) => data)
  }

  authorize(redirect: string | null) {
    const state = redirect ? `&state=${redirect}` : ''
    window.location.replace(`/api/oauth2/authorize?client_id=charm&response_type=code${state}`)
  }

  logout() {
    window.location.replace('/api/auth/logout')
  }

  refresh(isExcludedFromLoadingBar = true) {
    const customData = {isExcludedFromLoadingBar, ignoreExpiration: true}

    return defaultApi
      .post('/api/auth/token/refresh', undefined, {
        redirectOn401: false,
        customData,
      })
      .then(({data}) => data)
  }

  reset(username: string): Promise<void> {
    return defaultApi.post<void>('/api/auth/password-resets', {username}).then(({data}) => data)
  }

  resetPassword(userId: number, data: ResetPassword): Promise<void> {
    try {
      this.#validateNewPassword(data)
    } catch (error) {
      return Promise.reject(error)
    }

    return defaultApi.post(`/api/auth/users/${userId}/credentials?reset=true`, data)
  }

  changePassword(userId: number, data: ChangePassword): Promise<void> {
    try {
      this.#validateNewPassword(data)
    } catch (error) {
      return Promise.reject(error)
    }

    return defaultApi.put(`/api/auth/users/${userId}/credentials`, data)
  }

  allPasswordsNotEmpty(...passwords: Maybe<string>[]) {
    return passwords.every((p) => isNotNullOrUndefined(p) && p.trim().length > 0)
  }

  scheduleRefresh() {
    if (refreshTimeoutId === undefined) {
      const curTime = Date.now()
      const difference = ExpiryCookie.get() - REFRESH_BEFORE_MS - curTime
      const limitedDifference = Math.max(1000, Math.min(difference, 2147483647))

      refreshTimeoutId = window.setTimeout(this._doRefresh.bind(this), limitedDifference)
    }
  }

  fetchUsers(
    ids: Maybe<number[]>,
    enabled?: boolean,
    pageable?: Pick<PageableRequest<any>, 'page' | 'size'>,
    roles?: Maybe<string[]>,
    companyIds?: Maybe<number[]>
  ) {
    return defaultApi
      .get<Page<EnabledUser>>('/api/auth/users', {
        params: {
          id: ids?.join(',') ?? null,
          enabled,
          role: roles?.join(',') ?? null,
          companyIds: companyIds?.join(',') ?? null,
          ...pageable,
        },
      })
      .then(({data}) => data)
  }

  updateUser(id: number, enabled: boolean, roles: string[]) {
    return defaultApi.put(`/api/auth/users/${id}`, {enabled, roles}).then(({data}) => data)
  }

  createUser(request: UserCreateRequest) {
    return defaultApi.post('/api/auth/users', request).then(({data}) => data)
  }

  removeAssignedCompany(userId: number, companyId: number) {
    return defaultApi.delete(`/api/auth/assigned-companies`, {params: {userId, companyId}})
  }

  assignCompanyByInvitation(invitation: InvitationConfirmRequest) {
    return defaultApi.post('/api/auth/assigned-companies', invitation)
  }
}

export default new AuthService()
